import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const stats = [
  { label: "Total Reviews", value: "47", icon: "ri-chat-1-line" },
  { label: "Average Rating", value: "4.9/5", icon: "ri-star-line" },
  { label: "5-Star Reviews", value: "44", icon: "ri-star-fill" },
  { label: "Counties Served", value: "6", icon: "ri-map-2-line" },
];

export default function Reviews() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Soft abstract green and beige organic shapes pattern, warm neutral tones with subtle gold accents, elegant calming background, minimal design, no text, gentle gradients, professional atmosphere"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-0.5 mb-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-8 h-8 flex items-center justify-center">
                <i className="ri-star-fill text-xl text-gold-accent" />
              </div>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Client Reviews
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Honest feedback from families across Illinois who trust Sycamore Oasis with their loved ones.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 md:py-10 bg-white border-b border-gray-100">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="text-center p-4 md:p-5 rounded-xl bg-soft-gray hover:bg-brand-green-50 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-xl text-brand-green-600`} />
                </div>
                <span className="text-2xl md:text-3xl font-bold text-brand-green-700 block mb-1">
                  {stat.value}
                </span>
                <span className="text-xs md:text-sm text-gray-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Embed */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="google-embed" ref={setRef("google-embed")}>
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
          <div className={animateClass("google-embed", "text-center mb-8 md:mb-10")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Verified Reviews
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Read Our Reviews on Google
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto">
              All of our reviews come from real families on Google Maps. See what they are saying about us directly on Google.
            </p>
          </div>
          <div className={animateClass("google-embed", "rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-white")}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2984.9!2d-88.2567613!3d41.7290238!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x17c800fd433d29%3A0xaa26cec045b66f09!2sSycamore%20Oasis%20Corp!5e0!3m2!1sen!2sus!4v1"
              width="100%"
              height="550"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Sycamore Oasis Google Reviews"
              className="w-full"
            />
          </div>
          <div className={animateClass("google-embed", "text-center mt-8")}>
            <a
              href="https://www.google.com/maps/place/Sycamore+Oasis+Corp/@41.7290238,-88.2567613,652m/data=!3m1!1e3!4m8!3m7!1s0x17c800fd433d29:0xaa26cec045b66f09!8m2!3d41.7290238!4d-88.2567613!9m1!1b1!16s%2Fg%2F11twvlbqj3?entry=ttu"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full border border-brand-green-200 hover:bg-brand-green-50 hover:border-brand-green-300 transition-all duration-200 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-google-fill text-brand-green-600" />
              </div>
              Open in Google Maps
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-google-fill text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Share Your Experience
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Have we had the privilege of caring for your family? Your feedback helps other families make informed decisions and helps us continue to improve our services.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <a
              href="https://www.google.com/maps/place/Sycamore+Oasis+Corp/@41.7290238,-88.2567613,652m/data=!3m1!1e3!4m8!3m7!1s0x17c800fd433d29:0xaa26cec045b66f09!8m2!3d41.7290238!4d-88.2567613!9m1!1b1!16s%2Fg%2F11twvlbqj3?entry=ttu"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Leave a Google Review
            </a>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}